import { CancelTokenSource } from 'axios';
import DeleteActionButton from 'components/DeleteActionButton';
import EditActionButton from 'components/EditActionButton';
import CheckBox from 'components/final-form/CheckBox';
import { DropdownOption } from 'components/final-form/dropdownUtils';
import RadioGroup from 'components/final-form/RadioGroup';
import { selectInput } from 'components/final-form/SelectInput';
import { textInput } from 'components/final-form/TextInput';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import ToolbarButton from "components/layout/ToolbarButton";
import { IndexedZipData } from 'components/patient/upsert/UpsertPatient';
import UpsertPatientGuarantorModal from "components/patient/UpsertPatientGuarantorModal";
import { sortZipData } from "components/patient/utils/patientUtils";
import moment from 'moment';
import React, { Component } from "react";
import { Field } from 'react-final-form';
import { MessageSourceProps, withMessages } from "react-message-source";
import { Grid, Header, RadioProps, Table } from "semantic-ui-react";
import axios from 'services/http';
import { searchZipDataForInput } from 'services/zipDataServices';
import styled from "styled-components";
import { ZipDataOrderBy } from 'ts-types/api.enums';
import { PatientGuarantorDto } from "ts-types/api.types";
import { IndexSignature } from 'utils/praxTypes';

const ScrollableTableContainer = styled.div`
  height: 175px;
  overflow-y: auto;
  width: 100%;
  border: 1px solid rgba(34,36,38,.15);
  box-shadow: none;
  border-radius: .28571429rem;

  & .ui.table {
    border: none;
  }

   & .ui.table thead tr:first-child > th {
      position: sticky !important;
      top: 0;
      z-index: 2;
      background: #fff;
  }

  & .ui.table tbody tr:last-child > td {
      border-bottom: 1px solid rgba(34,36,38,.15)
  }
`;

const StyledHeader = styled(Header)`
  &.padding-top {
    padding-top: 18px;
  }
`;

interface Props extends MessageSourceProps {
  mapRowsToFields: (rows: Array<Array<string>>, pane: string) => Array<JSX.Element>,
  initialCities: Array<DropdownOption>,
  initialZip: Array<DropdownOption>,
  genders: Array<RadioProps>,
  countries: Array<RadioProps>,
  countryId: number,
  patientGuarantors: Array<PatientGuarantorDto>,
  addPatientGuarantor: (patientGuarantor: PatientGuarantorDto) => void,
  editPatientGuarantor: (patientGuarantorIndex: number, patientGuarantor: PatientGuarantorDto) => void,
  deletePatientGuarantor: (patientGuarantorIndex: number) => void
}

interface State {
  cities: Array<DropdownOption>,
  zip: Array<DropdownOption>,
  modalOpen: boolean,
  patientGuarantorDtoIndex: number,
  cancelTokenSource: CancelTokenSource
}

class InsurancePane extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const {initialCities, initialZip} = props;

    this.state = {
      cities: initialCities,
      zip: initialZip,
      modalOpen: false,
      patientGuarantorDtoIndex: -1,
      cancelTokenSource: axios.CancelToken.source()
    }
  }

  componentDidUpdate({initialCities: prevCities, initialZip: prevZip}: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {

    const {initialCities, initialZip} = this.props;

    if (prevCities.length != initialCities.length || prevZip.length != initialZip.length) {
      this.setState({
        zip: initialZip,
        cities: initialCities
      })
    }
  }

  toggleModal = () => {
    this.setState(({modalOpen}: State) => ({
      modalOpen: !modalOpen
    }))
  };

  addInsuranceInformation = () => {
    this.setState({
      patientGuarantorDtoIndex: -1
    });

    this.toggleModal();
  };

  editInsuranceInformation = (index: number) => {
    this.setState({
      patientGuarantorDtoIndex: index
    });

    this.toggleModal();
  };

  onUpsertPatientGuarantor = (patientGuarantor: PatientGuarantorDto) => {

    const {addPatientGuarantor, editPatientGuarantor} = this.props;
    const {patientGuarantorDtoIndex} = this.state;

    if (patientGuarantorDtoIndex != -1) {
      editPatientGuarantor(patientGuarantorDtoIndex, patientGuarantor);
    } else {
      addPatientGuarantor(patientGuarantor);
    }

    this.toggleModal();
  };

  searchZipData = (query: string) => {

    const {countryId} = this.props;
    const {cities, zip: zips, cancelTokenSource} = this.state;

    searchZipDataForInput(query, countryId, cancelTokenSource, 75)
        .then(response => {

          const allInitialAndNewCityAdditions = cities.filter(city => city.value == city.key);
          const allInitialAndNewZipAdditions = zips.filter(zip => zip.value == zip.key);

          this.setState({
            cities: [...allInitialAndNewCityAdditions, ...this.mapZipData("city", response, ZipDataOrderBy.CITY)],
            zip: [...allInitialAndNewZipAdditions, ...this.mapZipData("zip", response, ZipDataOrderBy.ZIP)]
          })
        })
        .catch(() => {
          this.setState({
            cities: [],
            zip: []
          })
        })
  };

  mapZipData = (field: string, zipData: Array<IndexedZipData>, orderBy: ZipDataOrderBy): Array<DropdownOption> => {

    const sortedZipData = sortZipData(zipData, orderBy);

    return sortedZipData.map(data => ({
      key: data.id,
      text: data[field],
      value: `${data.zip}, ${data.city}, ${data.countryId}`,
      content: `${data.zip}, ${data.city}, ${data.canton}`,
      predefinedOption: true
    }))
  };

  onNewAddition = (options: "zip" | "cities") => (newAddition: DropdownOption) => {

    const {cities, zip: zips} = this.state;

    if (options == "cities") {

      const foundCity = cities.find(city => city.key == newAddition.key);

      if (!foundCity) {
        newAddition.predefinedOption = false;
        const newCities = cities.filter(city => city.predefinedOption === true);
        this.setState({
          cities: [newAddition, ...newCities]
        });
      }
    } else if (options == "zip") {

      const foundZip = zips.find(zip => zip.key === newAddition.key);

      if (!foundZip) {
        newAddition.predefinedOption = false;
        const newZips = zips.filter(city => city.predefinedOption == true);
        this.setState({
          zip: [newAddition, ...newZips]
        });
      }
    }
  };

  mapFieldRows = (fields: Array<any>, messageKey?: string) => {

    const {getMessage} = this.props;

    return fields.map((fieldProps, index) => {
      let labelKey = messageKey ? `${messageKey}.${fieldProps.name}` : fieldProps.name;
      if (fieldProps.name.charAt(0) === "b") {
        labelKey = `${messageKey}.${fieldProps.name.substring(1, fieldProps.name.length)}`;
      }
      return (
          <Grid.Row key={index}>
            <Grid.Column width={5} verticalAlign="middle">
              {getMessage(labelKey)}:
            </Grid.Column>
            <Grid.Column width={11} verticalAlign="middle">
              <Field
                  fluid
                  {...fieldProps}
              />
            </Grid.Column>
          </Grid.Row>
      );
    });
  };

  render(): React.ReactNode {
    return (
        <FormContainer>
          <FormContent>
            <PraxFormGrid>
              {this.renderInsuranceInformationSection()}
              {this.renderPaymentInformationSection()}
              {this.renderProfessionalAndParentsSection()}
            </PraxFormGrid>
          </FormContent>
        </FormContainer>
    );
  }

  renderInsuranceInformationSection = (): JSX.Element => {

    const {getMessage, mapRowsToFields, patientGuarantors, deletePatientGuarantor} = this.props;
    const {patientGuarantorDtoIndex, modalOpen} = this.state;

    const patientGuarantor = patientGuarantorDtoIndex != -1 ? patientGuarantors[patientGuarantorDtoIndex] : undefined;

    const rows = [["representingDoctorId"]];

    const mappedPatientGuarantors = patientGuarantors.map((patientGuarantor, index) => {

      return (
          <Table.Row key={index}>
            <Table.Cell>{patientGuarantor.tariff}</Table.Cell>
            <Table.Cell>{patientGuarantor.guarantor}</Table.Cell>
            <Table.Cell>
              {patientGuarantor.fromDate && moment(patientGuarantor.fromDate).format("DD.MM.YYYY")}
            </Table.Cell>
            <Table.Cell>
              {patientGuarantor.toDate && moment(patientGuarantor.toDate).format("DD.MM.YYYY")}
            </Table.Cell>
            <Table.Cell>
              <EditActionButton
                  hoverMessage={getMessage('patient.editInsuranceInformation')}
                  onConfirm={() => this.editInsuranceInformation(index)}
              />
              <DeleteActionButton
                  hoverMessage={getMessage("patient.deleteInsuranceInformation")}
                  popupMessage={getMessage("patient.confirmDeleteInsuranceInformation")}
                  onConfirm={() => deletePatientGuarantor(index)}
              />
            </Table.Cell>
          </Table.Row>
      )
    });

    return (
        <React.Fragment>
          <Grid.Row>
            <Grid.Column verticalAlign="middle" width={4}>
              <Header>{getMessage("insuranceInformation")}</Header>
            </Grid.Column>
            <Grid.Column verticalAlign="middle" textAlign="right" width={12}>
              <ToolbarButton iconName="plus"
                             iconSize="large"
                             onClick={this.addInsuranceInformation}
                             dataTooltip={getMessage("patient.addInsuranceInformation")}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <ScrollableTableContainer>
                <Table celled size="small">
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>{getMessage("tariff")}</Table.HeaderCell>
                      <Table.HeaderCell>{getMessage("guarantor")}</Table.HeaderCell>
                      <Table.HeaderCell>{getMessage("validAfter")}</Table.HeaderCell>
                      <Table.HeaderCell>{getMessage("validBefore")}</Table.HeaderCell>
                      <Table.HeaderCell>{getMessage("actions")}</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {mappedPatientGuarantors}
                  </Table.Body>
                </Table>
              </ScrollableTableContainer>
            </Grid.Column>
          </Grid.Row>
          {mapRowsToFields(rows, "insurance")}
          <UpsertPatientGuarantorModal
              modalOpen={modalOpen}
              onModalClose={this.toggleModal}
              patientGuarantorDto={patientGuarantor}
              onUpsertPatientGuarantor={this.onUpsertPatientGuarantor}
          />
        </React.Fragment>

    )
  };

  renderPaymentInformationSection = (): JSX.Element => {

    const {getMessage, genders, countries} = this.props;
    const {cities, zip} = this.state;

    const selectMap: IndexSignature = {
      bcity: cities,
      bzip: zip,
      bcountryId: countries
    };

    const paymentInfoLeftFields = [
      textInput({
        name: "btitle"
      }),
      textInput({
        name: "bname"
      }),
      textInput({
        name: "bfirstName"
      }),
      textInput({
        name: "bposition"
      })
    ];

    const paymentInfoRightFields = [
      textInput({
        name: "bstreet"
      }),
      selectInput({
            name: "bzip",
            allowAdditions: true,
            remoteSearch: true,
            customRemoteSearchMethod: (this.searchZipData),
            onNewAddition: (this.onNewAddition("zip"))
          },
          selectMap
      ),
      selectInput({
            name: "bcity",
            allowAdditions: true,
            remoteSearch: true,
            customRemoteSearchMethod: (this.searchZipData),
            onNewAddition: (this.onNewAddition("cities"))
          },
          selectMap
      ),
      selectInput({
            name: "bcountryId"
          },
          selectMap
      )
    ];

    const mappedLeftRow = this.mapFieldRows(paymentInfoLeftFields, "patient");
    const mappedRightRow = this.mapFieldRows(paymentInfoRightFields, "patient");

    return (
        <React.Fragment>
          <Grid.Row>
            <Grid.Column>
              <Header>{getMessage("patient.paymentInformation")}</Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={8}>
              <PraxFormGrid>
                <Grid.Row>
                  <Grid.Column width={5} verticalAlign="middle">
                  </Grid.Column>
                  <Grid.Column width={11} verticalAlign="middle">
                    <Field
                        name="separateBill"
                        component={CheckBox}
                        label={getMessage("patient.separateBill")}
                    />
                  </Grid.Column>
                </Grid.Row>
              </PraxFormGrid>
            </Grid.Column>
            <Grid.Column width={8}>
              <PraxFormGrid>
                <Grid.Row>
                  <Grid.Column width={5} verticalAlign="middle">
                  </Grid.Column>
                  <Grid.Column width={11} verticalAlign="middle">
                    <Field
                        name="reminder"
                        component={CheckBox}
                        label={getMessage("patient.reminder")}
                    />
                  </Grid.Column>
                </Grid.Row>
              </PraxFormGrid>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={8}>
              <PraxFormGrid>
                <Grid.Row>
                  <Grid.Column width={5} verticalAlign="middle">
                    {getMessage("patient.sex")}
                  </Grid.Column>
                  <Grid.Column width={11} verticalAlign="middle">
                    <Field
                        name="bsex"
                        component={RadioGroup}
                        radioDefinitions={genders}
                        radiowidth="50%"
                    />
                  </Grid.Column>
                </Grid.Row>
              </PraxFormGrid>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={8}>
              <PraxFormGrid>
                {mappedLeftRow}
              </PraxFormGrid>
            </Grid.Column>
            <Grid.Column width={8}>
              <PraxFormGrid>
                {mappedRightRow}
              </PraxFormGrid>
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    );
  };

  renderProfessionalAndParentsSection = (): JSX.Element => {

    const {getMessage} = this.props;

    const professionalFields = [
      textInput({
        name: "profession"
      }),
      textInput({
        name: "employer"
      })
    ];

    const parentFields = [
      textInput({
        name: "mother"
      }),
      textInput({
        name: "father"
      })
    ];

    const mappedProfessionalRow = this.mapFieldRows(professionalFields, "patient");
    const mappedParentRow = this.mapFieldRows(parentFields, "patient");

    return (
        <React.Fragment>
          <Grid.Row>
            <Grid.Column width={8}>
              <PraxFormGrid>
                <Grid.Row>
                  <Grid.Column>
                    <StyledHeader className="padding-top">{getMessage("patient.professional")}</StyledHeader>
                  </Grid.Column>
                </Grid.Row>
                {mappedProfessionalRow}
              </PraxFormGrid>
            </Grid.Column>
            <Grid.Column width={8}>
              <PraxFormGrid>
                <Grid.Row>
                  <Grid.Column>
                    <StyledHeader className="padding-top">{getMessage("patient.parents")}</StyledHeader>
                  </Grid.Column>
                </Grid.Row>
                {mappedParentRow}
              </PraxFormGrid>
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    );
  };
}

export default withMessages(InsurancePane);
